/**
 * components/brief-builder/StepIndicator.tsx
 * Horizontal stepper showing the user's progress through the brief-builder workflow.
 */
import type { BriefStage } from '@/types/brief-builder'
import { BRIEF_BUILDER_STEPS, BRIEF_ACTIVE_STEP } from '@/types/brief-builder'

interface Props {
  stage: BriefStage
}

export default function StepIndicator({ stage }: Props) {
  const activeIndex = BRIEF_ACTIVE_STEP[stage] ?? 0

  return (
    <div className="w-full bg-white border-b border-slate-100 px-5 py-4">
      <div className="max-w-4xl mx-auto">
        <ol className="flex items-start gap-0">
          {BRIEF_BUILDER_STEPS.map((step, i) => {
            const done    = i < activeIndex
            const current = i === activeIndex
            return (
              <li key={step.id} className="flex items-start flex-1">
                {/* Circle + label */}
                <div className="flex flex-col items-center gap-1 flex-1">
                  <div
                    className={`
                      w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                      transition-colors shrink-0
                      ${done    ? 'bg-emerald-500 text-white'          : ''}
                      ${current ? 'bg-indigo-600 text-white ring-2 ring-indigo-200' : ''}
                      ${!done && !current ? 'bg-slate-100 text-slate-400' : ''}
                    `}
                  >
                    {done ? (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                        <path d="M2.5 7l3.5 3.5 5.5-6" stroke="currentColor"
                              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <span>{i + 1}</span>
                    )}
                  </div>
                  <span className={`text-[10px] font-medium text-center leading-tight w-16 min-h-[2rem]
                    ${current ? 'text-indigo-700' : done ? 'text-emerald-600' : 'text-slate-400'}
                  `}>
                    {step.label}
                  </span>
                </div>

                {/* Connector line (except after last) — sits at circle height */}
                {i < BRIEF_BUILDER_STEPS.length - 1 && (
                  <div className={`flex-none h-0.5 w-full max-w-[40px] mt-[13px]
                    ${i < activeIndex ? 'bg-emerald-400' : 'bg-slate-200'}
                  `} />
                )}
              </li>
            )
          })}
        </ol>
      </div>
    </div>
  )
}
